import React, { useState } from 'react';
import axios from 'axios';
import {
  CircularProgress,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Modal from 'molecules/Modal';
import { ModalContent } from 'molecules/Modal/Modal.styles';
import { neutrals } from 'utils/styles/color';
import { palette } from 'utils/styles/variables';

type InvoicePaymentLinkActionProps = {
  invoiceId: string;
  invoiceNumber: string;
  onError: (message: string) => void;
};

type InvoicePaymentLinks = {
  card_payment_link: string;
  bank_debit_payment_link: string;
};

type PaymentLinkType = 'card' | 'bankDebit';

const paymentLinkLabelStyles: React.CSSProperties = {
  flexShrink: 0,
  color: neutrals[800],
  fontFamily: 'Poppins, sans-serif',
  fontSize: '0.875rem',
  fontWeight: 500,
  lineHeight: '22px',
};

const paymentLinkStyles: React.CSSProperties = {
  minWidth: 0,
  color: palette.primary.main,
  fontFamily: 'Poppins, sans-serif',
  fontSize: '0.875rem',
  fontWeight: 400,
  lineHeight: '22px',
  overflowWrap: 'anywhere',
};

const InvoicePaymentLinkAction = ({
  invoiceId,
  invoiceNumber,
  onError,
}: InvoicePaymentLinkActionProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [loading, setLoading] = useState(false);
  const [paymentLinks, setPaymentLinks] = useState<InvoicePaymentLinks | null>(
    null
  );
  const [copiedLinks, setCopiedLinks] = useState<
    Record<PaymentLinkType, boolean>
  >({ card: false, bankDebit: false });
  const buttonId = `invoice-${invoiceId}-actions-button`;
  const menuId = `invoice-${invoiceId}-actions-menu`;

  const handleGeneratePaymentLink = async () => {
    setAnchorEl(null);
    setLoading(true);

    try {
      const { data } = await axios.post<InvoicePaymentLinks>(
        `${process.env.REACT_APP_BILLING_SERVICE}/payment/create-invoice-payment-links`,
        { invoice_id: invoiceId }
      );

      if (!data.card_payment_link || !data.bank_debit_payment_link) {
        throw new Error('Payment links were not returned');
      }

      setPaymentLinks(data);
    } catch (error) {
      onError('Unable to generate the payment link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyPaymentLink = async (
    paymentLink: string,
    paymentLinkType: PaymentLinkType
  ) => {
    setCopiedLinks((currentCopiedLinks) => ({
      ...currentCopiedLinks,
      [paymentLinkType]: false,
    }));

    try {
      await navigator.clipboard.writeText(paymentLink);
      setCopiedLinks((currentCopiedLinks) => ({
        ...currentCopiedLinks,
        [paymentLinkType]: true,
      }));
    } catch (error) {
      onError('Unable to copy the payment link. Please try again.');
    }
  };

  const handleCloseModal = () => {
    setPaymentLinks(null);
    setCopiedLinks({ card: false, bankDebit: false });
  };

  return (
    <>
      <IconButton
        id={buttonId}
        aria-label={`Actions for invoice #${invoiceNumber}`}
        aria-controls={anchorEl ? menuId : undefined}
        aria-expanded={anchorEl ? 'true' : undefined}
        aria-haspopup="menu"
        disabled={loading}
        onClick={(event) => {
          event.stopPropagation();
          setAnchorEl(event.currentTarget);
        }}
        size="small"
      >
        {loading ? <CircularProgress size={20} /> : <MoreVertIcon />}
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        onClick={(event) => event.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        MenuListProps={{
          id: menuId,
          'aria-labelledby': buttonId,
        }}
      >
        <MenuItem
          onClick={(event) => {
            event.stopPropagation();
            handleGeneratePaymentLink();
          }}
        >
          Generate Payment Link
        </MenuItem>
      </Menu>

      <Modal
        open={Boolean(paymentLinks)}
        maxWidth="sm"
        title={`Invoice #${invoiceNumber} payment link`}
        onClose={handleCloseModal}
        onClick={(event) => event.stopPropagation()}
        sx={{ zIndex: 33333 }}
      >
        <Stack gap="24px">
          <ModalContent>
            {paymentLinks && (
              <Stack gap="16px">
                <Stack
                  role="group"
                  aria-label="Card payment link"
                  style={{ flexDirection: 'column', gap: '4px' }}
                >
                  <Typography style={paymentLinkLabelStyles}>
                    Card Payment Link:
                  </Typography>
                  <Stack direction="row" alignItems="center" gap="8px">
                    <Link
                      href={paymentLinks.card_payment_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={paymentLinkStyles}
                    >
                      {paymentLinks.card_payment_link}
                    </Link>
                    <IconButton
                      aria-label="Copy card payment link"
                      onClick={() =>
                        handleCopyPaymentLink(
                          paymentLinks.card_payment_link,
                          'card'
                        )
                      }
                      size="small"
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                    {copiedLinks.card && (
                      <Typography role="status" variant="body2">
                        Copied
                      </Typography>
                    )}
                  </Stack>
                </Stack>
                <Stack
                  role="group"
                  aria-label="Bank debit payment link"
                  style={{
                    flexDirection: 'column',
                    gap: '4px',
                    marginBottom: 20,
                  }}
                >
                  <Typography style={paymentLinkLabelStyles}>
                    Bank Debit Link:
                  </Typography>
                  <Stack direction="row" alignItems="center" gap="8px">
                    <Link
                      href={paymentLinks.bank_debit_payment_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={paymentLinkStyles}
                    >
                      {paymentLinks.bank_debit_payment_link}
                    </Link>
                    <IconButton
                      aria-label="Copy bank debit payment link"
                      onClick={() =>
                        handleCopyPaymentLink(
                          paymentLinks.bank_debit_payment_link,
                          'bankDebit'
                        )
                      }
                      size="small"
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                    {copiedLinks.bankDebit && (
                      <Typography role="status" variant="body2">
                        Copied
                      </Typography>
                    )}
                  </Stack>
                </Stack>
              </Stack>
            )}
          </ModalContent>
        </Stack>
      </Modal>
    </>
  );
};

export default InvoicePaymentLinkAction;
